import React from "react";

/**
 * Bespoke Toukky logo.
 * Design concept: a "journey" mark — origin dot arcing up to a paper-plane tip.
 * Not a generic plane icon; represents connection between places.
 *
 * Colors are locked to Toukky's identity (red #C1121F on white; white on solid red).
 * The letters of the wordmark are ink (#0B1220) on light surfaces (owner,
 * 2026-10-06: "en noir", after the night blue of 2026-10-04), white on dark
 * ones; the badge and the dot stay red.
 */

// LogoMark — icon only. Sizes with the parent's font-size or explicit `size` prop.
export function LogoMark({ size = 32, variant = "solid", className = "", ...rest }) {
  // variant: "solid" (white mark on red badge) | "outline" (red mark on white)
  // | "ink" (red mark on an ink badge: the Toukky Partner app's logo, owner
  // 2026-11-01: black, as the apps' first logo was)
  const bg = variant === "solid" ? "#C1121F" : variant === "ink" ? "#0B1220" : "#FFFFFF";
  const fg = variant === "solid" ? "#FFFFFF" : "#C1121F";
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="Toukky"
      {...rest}
    >
      {/* Rounded badge */}
      <rect x="0" y="0" width="32" height="32" rx="8" ry="8" fill={bg} />

      {/* Route arc — bottom-left → top-right */}
      <path
        d="M 8 23 Q 14 8 22.5 11"
        stroke={fg}
        strokeWidth="2.8"
        strokeLinecap="round"
        fill="none"
      />

      {/* Origin dot */}
      <circle cx="8" cy="23" r="2.8" fill={fg} />

      {/* Destination — paper-plane silhouette */}
      <path
        d="M 26.4 6.6 L 21 12 L 22.6 12.4 L 22.8 15.6 L 24.4 13.4 L 27.2 14 Z"
        fill={fg}
      />
      <path
        d="M 26.4 6.6 L 22.6 12.4 L 24 12.7 Z"
        fill={fg}
        opacity="0.65"
      />
    </svg>
  );
}

// LogoWordmark — the "toukky." typography (uses Figtree from index.html).
export function LogoWordmark({ className = "" }) {
  return (
    // dir="ltr": the brand reads "toukky." in Arabic pages too, never ".toukky".
    <span dir="ltr" translate="no" className={`font-brand font-extrabold tracking-tight leading-none ${className}`}>
      toukky<span className="text-touki-red">.</span>
    </span>
  );
}

// LogoFull — combined mark + wordmark, standard header/footer usage.
export default function Logo({ size = 32, wordmarkClass = "text-xl text-touki-black", variant = "solid", className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <LogoMark size={size} variant={variant} />
      <LogoWordmark className={wordmarkClass} />
    </span>
  );
}
